import React, { useEffect, useMemo, useState } from "react"
import moment from "moment"
import "moment/locale/fr"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../../utilities/Constants"
import { getLieuAffichage } from "../../../utilities/depLieux"

// Grille d'un jour du planning dispatch (V2, langage visuel de l'Agenda).
// La mécanique est inchangée : drag & drop des missions sur un créneau,
// resize au pas de 30 min (TIME_SLOT_HEIGHT = 40 px par 30 min — le calcul
// de resize du parent en dépend), spinner par mission pendant le PUT.
const TIME_SLOT_HEIGHT = 40
const START_HOUR = 7
const END_HOUR = 18
// Gouttière des heures + géométrie des cartes.
const GUTTER = 64
const CARD_W = 200
const CARD_GAP = 8
// Couleurs de l'état RENDEZ_VOUS (mêmes valeurs que DepMissionCardV2).
const RDV_COLOR = "#6D28D9"
const RDV_BG = "#EDE9FE"

// Retour client 15.10 : la carte dit où l'on va. Lieu court d'un POI pour
// une ligne « départ → arrivée » : label (parc, garage…), sinon localité,
// sinon 1re ligne du texte brut (même source que DepMissionCardV2).
export const lieuCourt = (po, fallbacks) => {
	const aff = getLieuAffichage(po, fallbacks)
	if (!aff) return ""
	if (po && !po.label && po.adresse && po.adresse.localite)
		return po.adresse.localite
	return aff.lines[0]
}

const contenuCarte = (dm) => {
	const depart = lieuCourt(dm.lieu_intervention, [
		dm.lieu_intervention_0,
		dm.lieu_intervention_1,
	])
	const arrivee = lieuCourt(dm.destination_finale, [
		dm.destination_finale_0,
		dm.destination_finale_1,
	])
	return {
		vehicule: `${dm.marque || ""} ${dm.modele || ""}`.trim() || "—",
		trajet: [depart, arrivee].filter(Boolean).join(" → "),
		diagnostic: dm.diagnostique_presume || "",
	}
}

const DepDispatchPlanning = ({
	data,
	index,
	addExtraHeight,
	onClickDm,
	onDropPlanning,
	resizeMissionRef,
	resizeMission,
	dmLoading,
}) => {
	moment.locale("fr")
	const [extraHeight, setExtraHeight] = useState(0)
	const now = moment()
	const [currentTime, setCurrentTime] = useState(
		now.hour() + now.minutes() / 60,
	)
	const [currentTimeLabel, setCurrentTimeLabel] = useState(
		now.format("HH:mm"),
	)
	const timeSlots = []
	for (let hour = START_HOUR; hour < END_HOUR; hour++) {
		for (let min = 0; min < 60; min += 30) {
			const time = `${hour.toString().padStart(2, "0")}:${min
				.toString()
				.padStart(2, "0")}`
			timeSlots.push(time)
		}
	}
	const gridHeight = timeSlots.length * TIME_SLOT_HEIGHT

	const onDragStartPlanning = (e, dmId) => {
		e.dataTransfer.setData("dmId", dmId.toString())
	}
	const onDragOverPlanning = (e) => {
		e.preventDefault()
	}
	const refreshCurrentTime = () => {
		const new_now = moment()
		setCurrentTime(new_now.hour() + new_now.minutes() / 60)
		setCurrentTimeLabel(new_now.format("HH:mm"))
	}
	const getTitle = () => {
		if (index === 0) return "Aujourd'hui"
		let str = moment(data.date, "DD-MM-YYYY").locale("fr").format("dddd")
		return str.charAt(0).toUpperCase() + str.slice(1)
	}
	const getMissionBornes = (mission) => {
		let [heure, minutes] = mission.date_rendez_vous.split(" ")[1].split(":")
		let bDebut = parseInt(heure) + parseInt(minutes) / 60
		return [bDebut, bDebut + parseInt(mission.delta_rendez_vous) / 60]
	}
	const getPositionedAppointments = useMemo(() => {
		const parsed = data.missions
			.map((appt) => ({
				...appt,
				start: moment(appt.date_rendez_vous, "DD-MM-YYYY HH:mm"),
				end: moment(appt.date_rendez_vous, "DD-MM-YYYY HH:mm").add(
					appt.delta_rendez_vous,
					"minutes",
				),
			}))
			.sort(
				(a, b) =>
					a.start - b.start ||
					b.delta_rendez_vous - a.delta_rendez_vous,
			)
		const columns = []
		parsed.forEach((appt) => {
			let placed = false
			for (const column of columns) {
				const lastAppt = column[column.length - 1]
				if (!lastAppt || appt.start >= lastAppt.end) {
					column.push(appt)
					placed = true
					break
				}
			}
			if (!placed) columns.push([appt])
		})
		return columns
	}, [data.missions])

	const onMouseDownDm = (e, dm) => {
		e.stopPropagation()
		e.preventDefault()
		resizeMissionRef.current = {
			resizing: true,
			dm,
			startY: e.clientY,
			delta: 0,
		}
	}

	const renderDepMission = (dm, colIndex) => {
		const [hDebut, hFin] = getMissionBornes(dm)
		const topPosition = (hDebut - START_HOUR) * TIME_SLOT_HEIGHT * 2
		const height = (dm.delta_rendez_vous / 30) * TIME_SLOT_HEIGHT - 4
		const isResizing = resizeMission.dm?.id === dm.id
		const autreEnResize =
			resizeMission.dm?.id !== undefined && resizeMission.dm.id !== dm.id
		const fin = moment(dm.date_rendez_vous, "DD-MM-YYYY HH:mm").add(
			dm.delta_rendez_vous,
			"minutes",
		)
		const debut = dm.date_rendez_vous.split(" ")[1]
		const c = contenuCarte(dm)
		const compacte = dm.delta_rendez_vous <= 30
		const infobulle = [
			`${debut} – ${fin.format("HH:mm")}`,
			c.vehicule,
			c.trajet,
			c.diagnostic && `🔧 ${c.diagnostic}`,
		]
			.filter(Boolean)
			.join("\n")
		return (
			<div
				key={`rdv_${dm.id}`}
				style={{
					...st.mission,
					top: topPosition + 1,
					left: GUTTER + CARD_GAP + colIndex * (CARD_W + CARD_GAP),
					height: isResizing ? height + resizeMission.delta : height,
					opacity: autreEnResize ? 0.35 : 1,
				}}
			>
				{dmLoading === dm.id ? (
					<div style={st.missionSpinner}>
						<FontAwesomeIcon
							icon={faSync}
							spin
							style={{ color: RDV_COLOR }}
						/>
					</div>
				) : (
					<>
						<div
							style={
								compacte
									? { ...st.missionCorps, padding: "2px 8px" }
									: st.missionCorps
							}
							title={infobulle}
							onClick={() => onClickDm(dm)}
							draggable={true}
							onDragStart={(e) => onDragStartPlanning(e, dm.id)}
						>
							{compacte ? (
								// 30 min : une seule ligne, le détail en
								// infobulle.
								<span style={st.missionLigne}>
									<span style={st.missionPlage}>{debut}</span>{" "}
									<b>{c.vehicule}</b>
									{c.trajet ? ` · ${c.trajet}` : ""}
								</span>
							) : (
								<>
									<span style={st.missionPlage}>
										{`${debut} – ${fin.format("HH:mm")}`}
									</span>
									<span style={st.missionTitre}>
										{c.vehicule}
									</span>
									{c.trajet && (
										<span style={st.missionLieu}>
											{c.trajet}
										</span>
									)}
									{c.diagnostic && (
										<span style={st.missionLieu}>
											{`🔧 ${c.diagnostic}`}
										</span>
									)}
								</>
							)}
						</div>
						{/* Poignée de resize (pas de 30 min). */}
						<div
							style={st.resizeZone}
							onMouseDown={(e) => onMouseDownDm(e, dm)}
							title="Étirer pour ajuster la durée"
						>
							<div style={st.resizeBarre} />
						</div>
					</>
				)}
			</div>
		)
	}

	useEffect(() => {
		let interval
		if (index === 0) {
			const firstPlanning = document.getElementById("planning_0")
			const firstPlanningRect = firstPlanning.getBoundingClientRect()
			setExtraHeight(
				firstPlanningRect.height +
					window.innerHeight -
					firstPlanningRect.bottom +
					5,
			)
			interval = setInterval(refreshCurrentTime, 1000)
		}
		return () => {
			if (interval) clearInterval(interval)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div
			id={`planning_${index}`}
			style={{
				...st.jour,
				...(index === 0 && addExtraHeight
					? { minHeight: extraHeight }
					: {}),
			}}
		>
			<div style={st.jourHeader}>
				{index === 0 && <span style={st.pastilleAujourdhui} />}
				<span style={st.jourTitre}>{getTitle()}</span>
				<span style={st.jourDate}>
					{moment(data.date, "DD-MM-YYYY").format("D MMMM")}
				</span>
			</div>

			<div style={{ ...st.grille, height: gridHeight }}>
				{/* Lignes de créneaux : chaque bande de 30 min est une zone de
				    dépôt sur TOUTE la largeur (avant : l'étiquette seule). */}
				{timeSlots.map((time, i) => {
					const heurePleine = time.endsWith(":00")
					return (
						<div
							key={`slot_${time}`}
							onDragOver={onDragOverPlanning}
							onDrop={(e) => onDropPlanning(e, data.date, time)}
							style={{
								...st.slot,
								top: i * TIME_SLOT_HEIGHT,
								borderTop: heurePleine
									? `1px solid ${COLORS.border}`
									: `1px dashed ${COLORS.bg}`,
							}}
						>
							{heurePleine && (
								<span style={st.slotLabel}>{time}</span>
							)}
						</div>
					)
				})}

				{/* Ligne « maintenant » + pastille d'heure vivante. */}
				{index === 0 &&
					START_HOUR <= currentTime &&
					currentTime < END_HOUR && (
						<div
							style={{
								...st.maintenant,
								top:
									(currentTime - START_HOUR) *
									TIME_SLOT_HEIGHT *
									2,
							}}
						>
							<span style={st.maintenantLabel}>
								{currentTimeLabel}
							</span>
						</div>
					)}

				{getPositionedAppointments.map((dms, colIndex) =>
					dms.map((dm) => renderDepMission(dm, colIndex)),
				)}
			</div>
		</div>
	)
}

const st = {
	jour: {
		backgroundColor: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 12,
		padding: "14px 16px 18px",
		boxSizing: "border-box",
	},
	jourHeader: {
		display: "flex",
		alignItems: "baseline",
		gap: 8,
		marginBottom: 12,
	},
	pastilleAujourdhui: {
		width: 8,
		height: 8,
		borderRadius: 99,
		backgroundColor: COLORS.accent,
		alignSelf: "center",
		flexShrink: 0,
	},
	jourTitre: { fontSize: 15, fontWeight: 700, color: COLORS.text },
	jourDate: { fontSize: 12, color: COLORS.textLight },
	grille: { position: "relative" },
	slot: {
		position: "absolute",
		left: 0,
		right: 0,
		height: TIME_SLOT_HEIGHT,
		boxSizing: "border-box",
	},
	slotLabel: {
		position: "absolute",
		top: -8,
		left: 0,
		width: GUTTER - 16,
		fontSize: 11,
		color: COLORS.textLight,
		fontVariantNumeric: "tabular-nums",
		backgroundColor: COLORS.surface,
		userSelect: "none",
	},
	maintenant: {
		position: "absolute",
		left: 0,
		right: 0,
		height: 0,
		borderTop: `2px solid ${COLORS.accent}`,
		zIndex: 3,
		pointerEvents: "none",
	},
	maintenantLabel: {
		position: "absolute",
		top: -9,
		left: 0,
		padding: "1px 7px",
		borderRadius: 99,
		fontSize: 10,
		fontWeight: 700,
		color: "#fff",
		backgroundColor: COLORS.accent,
		fontVariantNumeric: "tabular-nums",
	},
	mission: {
		position: "absolute",
		width: CARD_W,
		zIndex: 2,
		backgroundColor: RDV_BG,
		borderLeft: `3px solid ${RDV_COLOR}`,
		borderRadius: 6,
		boxSizing: "border-box",
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
		transition: "opacity 0.12s",
	},
	missionSpinner: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		flex: 1,
	},
	missionCorps: {
		flex: 1,
		minHeight: 0,
		padding: "4px 8px",
		cursor: "pointer",
		display: "flex",
		flexDirection: "column",
		gap: 1,
		overflow: "hidden",
	},
	missionPlage: {
		fontSize: 10,
		fontWeight: 700,
		color: RDV_COLOR,
		fontVariantNumeric: "tabular-nums",
	},
	missionTitre: {
		fontSize: 12,
		fontWeight: 700,
		color: COLORS.text,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	missionLigne: {
		fontSize: 11,
		color: COLORS.text,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	missionLieu: {
		fontSize: 11,
		color: COLORS.textMuted,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	resizeZone: {
		height: 10,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "ns-resize",
		flexShrink: 0,
	},
	resizeBarre: {
		width: 22,
		height: 3,
		borderRadius: 99,
		backgroundColor: "rgba(109, 40, 217, 0.35)",
	},
}

export default DepDispatchPlanning
